import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import API from '@/apiRequest';
import ClientApp from '@/ClientApp';
import AccessDenied from '@/components/AccessDenied';
import CommercialPage from '@/components/CommercialPage';
import Toast from '@/components/common/Toast';
import MarketplacePage from '@/components/MarketplacePage';
import Page from '@/components/Page';
import ScreenLoading from '@/components/ScreenLoading';
import { TitleUpdater } from '@/components/common';
import { FORBIDDEN_REDIRECT_URL } from '@/config';
import getUserLanguage from '@/config/language';
import { AsyncServiceProvider } from '@/contexts/AsyncServiceContext';
import { ConfirmAlertProvider } from '@/contexts/ConfirmAlertContext';
import UserContext from '@/contexts/UserContext';
import { OrderViewProvider } from '@/contexts/OrderViewContext';
import FranchiseeApp from '@/FranchiseeApp';
import getSupportPanelLink from '@/helpers/get-support-panel-link';
import { checkAccessTokenIsExpired } from '@/services/isTokenExpired';
import * as divergencesDuck from '@/store/ducks/divergences';
import * as tagsDuck from '@/store/ducks/tags';
import messagesPTBR from '@/translations/pt_br.json';
import ClientsPublicPage from '@/components/ClientsPublicPage';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isPublicRoute = location.pathname.startsWith('/new-client');

  const [state, setState] = useState({
    localeConfig: {
      locale: 'pt-BR',
      messages: messagesPTBR,
    },
    authRole: '',
    name: '',
    features: [],
    ordersChanged: false,
    accountBalance: null,
    inspectionsTemplateLoaded: false,
    uniqueId: null,
    clientDocument: null,
    acceptedTerms: null,
    franchiseeId: null,
    type: null,
    contracts: null,
    profile: null,
    panelId: null,
    plan: null,
    identifier: null,
    dashboards: null,
    disabledByModality: false,
    modality: null,
    address: null,
    activeIntegrations: [],
  });

  const { data: divergencesCount } = useQuery({
    queryKey: ['orderDivergencesCount', state.authRole],
    queryFn: async () => {
      const { data } = await API.get('/count/order-divergences', {
        params: {
          status:
            state.authRole === 'ROLE_FRANCHISEE'
              ? ['WAITING_FRANCHISEE_FEEDBACK', 'WAITING_FRANCHISEE_AGREEMENT']
              : ['WAITING_CLIENT_FEEDBACK'],
        },
      });
      return data?.count ?? 0;
    },
    enabled:
      !isPublicRoute &&
      (state.authRole === 'ROLE_FRANCHISEE' || state.authRole === 'ROLE_CLIENT') &&
      state.features?.includes('DIVERGENCE_PANEL'),
    staleTime: 60 * 60 * 1000, // 1 hour cache
    gcTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (divergencesCount !== undefined) {
      dispatch(divergencesDuck.setCount(divergencesCount));
    }
  }, [divergencesCount, dispatch]);

  useEffect(() => {
    if (isPublicRoute) {
      return;
    }
    const fetchData = async () => {
      try {
        if (checkAccessTokenIsExpired()) {
          return;
        }

        const cachedMeStr = window.sessionStorage.getItem('me');
        if (cachedMeStr) {
          const cachedMe = JSON.parse(cachedMeStr);
          const needsTemplates =
            cachedMe.authRole !== 'ROLE_SUPPORT' &&
            cachedMe.authRole !== 'ROLE_INSPECTOR' &&
            cachedMe.status !== 'disabled';
          const cachedTemplatesStr = window.sessionStorage.getItem('orderTypeConfigurations');

          if (!needsTemplates || cachedTemplatesStr) {
            API.defaults.headers.common['notificationToken'] = `individual_${cachedMe.uniqueId}`;
            API.defaults.headers.common['Individual-UUID'] = `individual_${cachedMe.uniqueId}`;
            API.defaults.headers.common['Panel-UUID'] = `panel_${cachedMe.panelId}`;

            if (cachedMe.status === 'disabled' || cachedMe.authRole === 'ROLE_INSPECTOR') {
              navigate('/access-denied');
            }

            const { locale, messages } = getUserLanguage(cachedMe?.profile?.country);
            const cachedActive = JSON.parse(window.sessionStorage.getItem('activeIntegrations') || '[]');
            setState(prevState => ({
              ...prevState,
              ...cachedMe,
              activeIntegrations: cachedActive,
              inspectionsTemplateLoaded: true,
              localeConfig: { locale, messages },
            }));

            if (cachedMe.status !== 'disabled') {
              if (cachedMe.authRole === 'ROLE_FRANCHISEE' || cachedMe.authRole === 'ROLE_CLIENT') {
                dispatch(tagsDuck.syncTags());
                if (location.pathname === '/') navigate('/orders');
              }
              if (cachedMe.authRole === 'ROLE_SUPPORT') {
                getSupportPanelLink();
              }
              if (cachedMe.authRole === 'ROLE_COMMERCIAL') {
                if (location.pathname === '/') navigate('/commercial');
              }
            }
            return;
          }
        }

        const { data } = await API.get('me');
        API.defaults.headers.common['notificationToken'] = `individual_${data.unique_id}`;
        API.defaults.headers.common['Individual-UUID'] = `individual_${data.unique_id}`;
        API.defaults.headers.common['Panel-UUID'] = `panel_${data.panel_id}`;

        if (data.status === 'disabled' || data.role === 'ROLE_INSPECTOR') {
          navigate('/access-denied');
        }

        let inspectionsTemplateLoaded = false;
        // Request inspections templates if not support user
        if (data.role !== 'ROLE_SUPPORT' && data.role !== 'ROLE_INSPECTOR' && data.status !== 'disabled') {
          try {
            const { data: orderTypeConfigurations } = await API.get('templates/inspections');
            window.sessionStorage.setItem('orderTypeConfigurations', JSON.stringify(orderTypeConfigurations));
            inspectionsTemplateLoaded = true;
          } catch (err) {
            console.debug('App GET /templates/inspections error ', err);
            inspectionsTemplateLoaded = true; // Still mark as loaded to proceed
          }
        } else {
          inspectionsTemplateLoaded = true;
        }

        const features = [...new Set(data.features)];
        const mappedFeatures = features.map(f => {
          if (f === 'ORDER_MARKETPLACE_DOCUSIGN') return 'VISTORIA_DOCUSIGN';
          if (f === 'ORDER_MARKETPLACE_BRY') return 'VISTORIA_BRY';
          if (f === 'ORDER_MARKETPLACE_FICHA_CERTA') return 'VISTORIA_FICHA_CERTA';
          if (f === 'ORDER_MARKETPLACE_REPARO_FACIL') return 'VISTORIA_REPARO_FACIL';
          if (f === 'ORDER_MARKETPLACE_CAPTURA360') return 'VISTORIA_CAPTURA360';
          return f;
        });

        const me = {
          authRole: data.role,
          name: data.name || 'Usuário',
          features: mappedFeatures,
          uniqueId: data.unique_id,
          accountBalance: data?.details?.availableStandardMeters,
          clientDocument: data?.details?.clientDocument,
          acceptedTerms: data?.details?.acceptedTerms,
          franchiseeId: data?.details?.franchiseeId,
          type: data?.details?.type,
          contracts: data?.details?.contracts,
          profile: data?.profile,
          panelId: data.panel_id,
          plan: data?.details?.plan,
          identifier: data?.identifier,
          dashboards: data?.details?.dashboards,
          disabledByModality: ['PAY_PER_USE', 'BASIC_PLAN'].includes(data?.details?.modality),
          modality: data?.details?.modality,
          address: data?.details?.address,
        };

        if (data.status !== 'disabled') {
          if (data.role === 'ROLE_FRANCHISEE' || data.role === 'ROLE_CLIENT') {
            dispatch(tagsDuck.syncTags());
            if (location.pathname === '/') navigate('/orders');
          }
          if (data.role === 'ROLE_SUPPORT') {
            getSupportPanelLink();
          }
          if (data.role === 'ROLE_COMMERCIAL') {
            if (location.pathname === '/') navigate('/commercial');
          }
        } else {
          me.status = data.status;
        }

        let activeIntegrations = [];
        try {
          const { data: marketplaceData } = await API.get('marketplace');
          activeIntegrations = marketplaceData
            .filter(s => s.enabled || s.status === 'ENABLED')
            .map(s => s.slug);
          window.sessionStorage.setItem('activeIntegrations', JSON.stringify(activeIntegrations));
        } catch (err) {
          console.debug('App GET /marketplace error ', err);
          activeIntegrations = JSON.parse(window.sessionStorage.getItem('activeIntegrations') || '[]');
        }

        window.sessionStorage.setItem('me', JSON.stringify(me));
        window.sessionStorage.setItem('uniqueId', me.uniqueId);

        const { locale, messages } = getUserLanguage(me?.profile?.country);
        setState(prevState => ({
          ...prevState,
          ...me,
          inspectionsTemplateLoaded,
          localeConfig: { locale, messages },
        }));
      } catch (err) {
        console.debug('App useEffect error ', err);
        if (err.response && err.response.status && err.response.status === 403) {
          window.location = FORBIDDEN_REDIRECT_URL;
          return;
        }
      }
    };

    fetchData();
  }, [dispatch, navigate, isPublicRoute]);

  useEffect(() => {
    if (isPublicRoute || !state.authRole) return;
    if (location.pathname === '/') {
      if (state.authRole === 'ROLE_FRANCHISEE' || state.authRole === 'ROLE_CLIENT') {
        navigate('/orders');
      } else if (state.authRole === 'ROLE_COMMERCIAL') {
        navigate('/commercial');
      }
    }
  }, [location.pathname, state.authRole, navigate, isPublicRoute]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await API.get('me');

      let activeIntegrations = [];
      try {
        const { data: marketplaceData } = await API.get('marketplace');
        activeIntegrations = marketplaceData
          .filter(s => s.enabled || s.status === 'ENABLED')
          .map(s => s.slug);
        window.sessionStorage.setItem('activeIntegrations', JSON.stringify(activeIntegrations));
      } catch (err) {
        console.debug('App refreshMe GET /marketplace error ', err);
        activeIntegrations = JSON.parse(window.sessionStorage.getItem('activeIntegrations') || '[]');
      }

      const features = [...new Set(data.features)];
      const mappedFeatures = features.map(f => {
        if (f === 'ORDER_MARKETPLACE_DOCUSIGN') return 'VISTORIA_DOCUSIGN';
        if (f === 'ORDER_MARKETPLACE_BRY') return 'VISTORIA_BRY';
        if (f === 'ORDER_MARKETPLACE_FICHA_CERTA') return 'VISTORIA_FICHA_CERTA';
        if (f === 'ORDER_MARKETPLACE_REPARO_FACIL') return 'VISTORIA_REPARO_FACIL';
        if (f === 'ORDER_MARKETPLACE_CAPTURA360') return 'VISTORIA_CAPTURA360';
        return f;
      });

      const me = {
        authRole: data.role,
        name: data.name || 'Usuário',
        features: mappedFeatures,
        uniqueId: data.unique_id,
        accountBalance: data?.details?.availableStandardMeters,
        clientDocument: data?.details?.clientDocument,
        acceptedTerms: data?.details?.acceptedTerms,
        franchiseeId: data?.details?.franchiseeId,
        type: data?.details?.type,
        contracts: data?.details?.contracts,
        profile: data?.profile,
        panelId: data.panel_id,
        plan: data?.details?.plan,
        identifier: data?.identifier,
        dashboards: data?.details?.dashboards,
        disabledByModality: ['PAY_PER_USE', 'BASIC_PLAN'].includes(data?.details?.modality),
        modality: data?.details?.modality,
        address: data?.details?.address,
      };

      if (data.status !== 'disabled') {
        // me status not overridden
      } else {
        me.status = data.status;
      }

      window.sessionStorage.setItem('me', JSON.stringify(me));
      window.sessionStorage.setItem('uniqueId', me.uniqueId);

      const { locale, messages } = getUserLanguage(me?.profile?.country);
      setState(prevState => ({
        ...prevState,
        ...me,
        activeIntegrations,
        localeConfig: { locale, messages },
      }));
    } catch (err) {
      console.debug('App.refreshMe error ', err);
    }
  }, []);

  const setOrdersChanged = useCallback(changed => {
    setState(prevState => ({ ...prevState, ordersChanged: changed }));
  }, []);

  const ordersUpdated = useCallback(() => {
    setState(prevState => ({ ...prevState, ordersChanged: false }));
  }, []);

  const userContextValue = useMemo(() => ({
    ...state,
    refreshMe,
  }), [state, refreshMe]);

  const {
    authRole,
    inspectionsTemplateLoaded,
    localeConfig,
    features,
    name,
    franchiseeId,
    ordersChanged,
    accountBalance,
    contracts,
  } = state;

  const meFromSession = window.sessionStorage.getItem('me');
  const orderTypeConfigurations = window.sessionStorage.getItem('orderTypeConfigurations');

  if (!isPublicRoute && ((!inspectionsTemplateLoaded && !orderTypeConfigurations) || !meFromSession || !authRole)) {
    return <ScreenLoading />;
  }

  return (
    <IntlProvider locale={localeConfig.locale} messages={localeConfig.messages}>
      <ConfirmAlertProvider>
        <AsyncServiceProvider setOrdersChanged={setOrdersChanged}>
          <UserContext.Provider value={userContextValue}>
            <OrderViewProvider>
              <TitleUpdater />
              <Routes>
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route path="/new-client/:hash/*" element={<ClientsPublicPage />} />

                <Route
                  path="/orders/*"
                  element={
                    authRole === 'ROLE_FRANCHISEE' ? (
                      <UserContext.Provider value={userContextValue}>
                        <FranchiseeApp
                          userName={name}
                          features={features}
                          ordersChanged={ordersChanged}
                          ordersUpdated={ordersUpdated}
                          franchiseeId={franchiseeId}
                        />
                      </UserContext.Provider>
                    ) : authRole === 'ROLE_CLIENT' ? (
                      <UserContext.Provider value={userContextValue}>
                        <ClientApp userName={name} features={features} balance={accountBalance} contracts={contracts} />
                      </UserContext.Provider>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route path="/marketplace/*" element={<MarketplacePage />} />

                <Route
                  path="/inspections/*"
                  element={
                    authRole === 'ROLE_FRANCHISEE' ? (
                      <UserContext.Provider value={userContextValue}>
                        <FranchiseeApp
                          userName={name}
                          features={features}
                          ordersChanged={ordersChanged}
                          ordersUpdated={ordersUpdated}
                          franchiseeId={franchiseeId}
                        />
                      </UserContext.Provider>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/commercial/*"
                  element={
                    authRole === 'ROLE_COMMERCIAL' ? (
                      <CommercialPage />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/:page/*"
                  element={
                    authRole === 'ROLE_FRANCHISEE' ? (
                      <Page />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="*"
                  element={
                    authRole === 'ROLE_FRANCHISEE' || authRole === 'ROLE_CLIENT' ? (
                      <Navigate to="/orders" replace />
                    ) : authRole === 'ROLE_COMMERCIAL' ? (
                      <Navigate to="/commercial" replace />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
              </Routes>
            </OrderViewProvider>

            <Toast />
          </UserContext.Provider>
        </AsyncServiceProvider>
      </ConfirmAlertProvider>
    </IntlProvider>
  );
};

export default App;
